import React, { useContext } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import {
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import dayjs from 'dayjs';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { WordLocalizeContext } from '@/contexts/WordLocalizeContext';

import MultiUnitProdOrMatBox from '../../Box/Rendering/MultiUnitProdOrMatBox';
import ProdOrMatQuantityInput from '../../Inputs/ProdOrMatQuantityInput';

/**
 * ฟอร์มสำหรับเลือกวัตถุพร้อมจำนวนไปทำอะไรสักอย่าง เช่น การย้ายวัตถุดิบ
 */
function SelectMaterialToMoveForm({
  control,
  controlKey = 'materialIn',
  watch,
  setValue,
  information,
  showProduct,
  rerender,
  setRerender,
}) {
  const { fields } = useFieldArray({ control, name: controlKey });
  const { findWord } = useContext(WordLocalizeContext);
  const imsSetting = information?.setting?.ims;

  return (
    <div>
      {' '}
      <Paper>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <div className="font-bold"> เลือก</div>
                </TableCell>
                {imsSetting?.display?.purchase_date && (
                  <TableCell>
                    <div className="font-bold">
                      {' '}
                      {findWord('purchase_date') || 'วันที่ซื้อ'}
                    </div>
                  </TableCell>
                )}
                <TableCell>
                  <div className="font-bold">
                    วันที่วัตถุดิบเข้าคลังปัจจุบัน
                  </div>
                </TableCell>
                <TableCell>
                  <div className="font-bold"> เลขอ้างอิง </div>
                </TableCell>
                <TableCell>
                  <div className="font-bold">วัตถุดิบ </div>
                </TableCell>
                {imsSetting?.display?.source && (
                  <TableCell>
                    <div className="font-bold">
                      {findWord('material_source') || 'แหล่งที่มา'}{' '}
                    </div>
                  </TableCell>
                )}
                <TableCell>
                  <div className="font-bold">จำนวนทั้งหมด</div>
                </TableCell>
                <TableCell>
                  <div className="font-bold">จำนวนคงเหลือ</div>
                </TableCell>
                <TableCell>
                  <div className="font-bold">ดำเนินการ</div>
                </TableCell>
                {showProduct && (
                  <TableCell>
                    <div className="font-bold">สินค้า</div>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {!_.isEmpty(fields) ? (
                _.map(fields, (row, index) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                    }}
                  >
                    <TableCell component="th" scope="row">
                      <div className="text-xs">
                        <Controller
                          control={control}
                          name={`${controlKey}[${index}].selected`}
                          render={({ field }) => (
                            <Checkbox {...field} defaultChecked={field.value} />
                          )}
                        />
                      </div>
                    </TableCell>
                    {imsSetting?.display?.purchase_date && (
                      <TableCell>
                        {dayjs(row?.purchase_date).format('DD/MM/BBBB')}
                      </TableCell>
                    )}
                    <TableCell>
                      {dayjs(row?.recipt_date).format('DD/MM/BBBB')}
                    </TableCell>
                    <TableCell>{row?.running_number}</TableCell>
                    <TableCell>{row?.material?.name}</TableCell>
                    {imsSetting?.display?.source && (
                      <TableCell>
                        {row?.source?.overall_code} {row?.source?.name}
                      </TableCell>
                    )}
                    <TableCell>
                      <MultiUnitProdOrMatBox
                        quantity={row?.quantity}
                        foundMaterial={row?.material}
                      />
                    </TableCell>
                    <TableCell>
                      <MultiUnitProdOrMatBox
                        quantity={row?.amount}
                        foundMaterial={row?.material}
                      />
                    </TableCell>
                    <TableCell>
                      <ProdOrMatQuantityInput
                        controllerName={`${controlKey}[${index}].amount`}
                        artificialControllerName={`${controlKey}[${index}].amount_artificial`}
                        index={index}
                        selectedUnitControllerName={`${controlKey}[${index}].amount_selected_unit`}
                        defaultValue={row?.amount}
                        control={control}
                        watch={watch}
                        setValue={setValue}
                        disabled={!watch(`${controlKey}[${index}].selected`)}
                        selectedProduct={row?.material}
                        reload={rerender}
                        setReload={setRerender}
                      />
                    </TableCell>
                    {showProduct && (
                      <TableCell>
                        {row?.prefer_product?.name}
                        <div>
                          {(
                            parseFloat(
                              watch(`${controlKey}[${index}].amount`),
                            ) / row?.prefer_product?.net
                          ).toFixed(2)}{' '}
                          {row?.prefer_product?.unit}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6}>
                    <div className="text-center">ไม่มีข้อมูล</div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
}

export default SelectMaterialToMoveForm;

SelectMaterialToMoveForm.propTypes = {
  control: PropTypes.object,
  /** คำที่จะเอาไปเป็น Key นำหน้าใน Array ของวัตถุดิบแต่ละตัว เช่น ถ้า key เป็น materialList
   * array แต่ละตัว key จะเป็น materialList[0].material, materialList[1].material  */
  controlKey: PropTypes.string,
  watch: PropTypes.func,
  setValue: PropTypes.func,
  information: PropTypes.object,
  showProduct: PropTypes.bool,
  rerender: PropTypes.bool,
  setRerender: PropTypes.func,
};
